import type { ManualPriceApprovalInput, ManualPriceInput, ManualPriceRejectionInput, ProposeLineFromJobInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import type { JobDetail } from '../../../jobs.api';
import { approveManualPrice, proposeLineFromManualPrice, rejectManualPrice, submitManualPrice } from '../manual-pricing.api';
import { useLatestManualPrice } from './use-latest-manual-price';

/** The server's own words for a refusal (a 409 says why the job cannot take a price now), else a generic line. */
function messageOf(err: unknown): string {
  const body = err instanceof ApiError ? (err.body as { message?: unknown } | null) : null;
  return typeof body?.message === 'string' ? body.message : 'Something went wrong — try again.';
}

type JobRef = Pick<JobDetail, 'id' | 'number' | 'pricingRequired'>;

/**
 * A job's off-card price (gitaumoses4/chukua#35): the latest request, and the clerk's submit, the pricer's
 * approve and reject, and the offer to add the approved price to the card. Failures stay inline as `error`
 * (a form's server error belongs next to the form); only success toasts, after the server confirms.
 */
export function useJobManualPrice(job: JobRef, reload: () => Promise<void>) {
  const { request, isLoaded, setRequest } = useLatestManualPrice(job.id, job.pricingRequired);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [offerLine, setOfferLine] = useState(false);
  const toast = useToast();

  async function run(action: () => Promise<void>): Promise<boolean> {
    setError(null);
    setSubmitting(true);
    try {
      await action();
      return true;
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError && err.status === 409) await reload();
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  const submit = (input: ManualPriceInput) =>
    run(async () => {
      setRequest(await submitManualPrice(job.id, input));
      toast.success(`Price for ${job.number} sent for approval`);
    });

  const approve = (input: ManualPriceApprovalInput) =>
    run(async () => {
      setRequest(await approveManualPrice(job.id, input));
      await reload();
      setOfferLine(true);
      toast.success(`Price for ${job.number} approved`);
    });

  const reject = (input: ManualPriceRejectionInput) =>
    run(async () => {
      setRequest(await rejectManualPrice(job.id, input));
      toast.success(`Price for ${job.number} rejected`);
    });

  const proposeLine = (input: ProposeLineFromJobInput) =>
    run(async () => {
      await proposeLineFromManualPrice(job.id, input);
      setOfferLine(false);
      toast.success(`Line from ${job.number} sent to the rate card approval queue`);
    });

  return { request, isLoaded, error, submitting, offerLine, submit, approve, reject, proposeLine, dismissOffer: () => setOfferLine(false) };
}
